import React, { FC, useState } from 'react';
import styles from './Cart.module.scss';
import { useUiString } from '../../../utils/hook/useUiString';

// Tipping lives here rather than on Square's side: the Web Payments SDK
// renders a card field and nothing else, so a hosted tip screen is not on
// offer. The tip is charged on top of the order total.

const PRESETS = [0, 10, 15, 20];

interface IProps {
    /** Percentages apply to the pre-tax subtotal, which is what guests
     *  expect a tip to be measured against. */
    subtotalCents: number;
    tipCents: number;
    disabled: boolean;
    onChange: (tipCents: number) => void;
}

export const TipSelector: FC<IProps> = ({ subtotalCents, tipCents, disabled, onChange }) => {
    const t = useUiString();
    const [custom, setCustom] = useState(false);

    const percentToCents = (percent: number): number =>
        Math.round((subtotalCents * percent) / 100);

    const activePreset = custom
        ? null
        : PRESETS.find((percent) => percentToCents(percent) === tipCents) ?? null;

    const chipClass = (active: boolean) =>
        active ? `${styles.chip} ${styles.chipActive}` : styles.chip;

    return (
        <div className={styles.field}>
            <span className={styles.label}>{t('addTip')}</span>
            <div className={styles.chips}>
                {PRESETS.map((percent) => (
                    <button
                        key={percent}
                        type="button"
                        disabled={disabled}
                        className={chipClass(activePreset === percent)}
                        onClick={() => { setCustom(false); onChange(percentToCents(percent)); }}
                    >
                        {percent === 0 ? t('noTip') : `${percent}%`}
                    </button>
                ))}
                <button
                    type="button"
                    disabled={disabled}
                    className={chipClass(custom)}
                    onClick={() => { setCustom(true); onChange(0); }}
                >
                    {t('customTip')}
                </button>
            </div>

            {custom && (
                <input
                    className={`${styles.input} ${styles.numberInput}`}
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    autoFocus
                    disabled={disabled}
                    onChange={(e) => {
                        const amount = Number.parseFloat(e.target.value);
                        onChange(Number.isFinite(amount) && amount > 0 ? Math.round(amount * 100) : 0);
                    }}
                />
            )}
        </div>
    );
};
